import { useMemo } from 'react';
import { HYUNDAI_CAPITAL_COMPANY_NO } from 'app/constants/rent';
import useAuth from 'app/hook/common/useAuth';
import useCars from 'app/hook/cars/useCars';
import useOption from 'app/hook/cars/useOption';
import useSsrWindowLocation from 'app/hook/common/useSsrWindowLocation';

export default function useService() {
	const location = useSsrWindowLocation();
	const { base, detailFlags, leaseSuccession, rent, rentSuccession } = useCars();
	const { choiceDataTotalPrice } = useOption();
	const { isLoggedIn, user } = useAuth();

	// 광고중
	const infoStatus = base.advertisement?.status;
	const isStatus = {
		sold: infoStatus === 'SOLD', // 삭제&판매 모두 해당
		wait: infoStatus === 'WAIT',
		advertise: infoStatus === 'ADVERTISE',
	};

	// 로그인
	const userId = isLoggedIn && user.id;
	const isNonAdMyCar = base.contact?.userId === userId && !isStatus.advertise;

	// 서비스
	const infoService = base.advertisement?.trust;
	const isService = useMemo(
		() => ({
			inspection: infoService?.indexOf('Inspection') > -1, // 현재 사용 안됨
			warranty: infoService?.indexOf('Warranty') > -1, // 진단
			extendWarranty: infoService?.indexOf('ExtendWarranty') > -1, // 보증 & 수입(보증)
			deemedExtendWarranty: !!(base.advertisement?.deemedExtendWarranty && base.manage?.dummy), // 보증(특정 조건충족 차량: 엔카종합보증 미광고 차량이지만 전용 광고 대상 엔카종합보증 판매가능한 미광고차량)
			homeService: infoService?.indexOf('HomeService') > -1,
			homeServiceDummy: infoService?.indexOf('HomeService') > -1 && base.manage?.dummy,
			compensate: infoService?.indexOf('Compensate') > -1, // 보상
			brandCertification: base.partnership?.brand !== undefined && base.partnership?.brand !== null,
			brandCertificationDomestic: base.partnership?.certifiedBrand,
			testDrive: !!base.partnership?.testdrive?.active,
			meetgo: infoService?.indexOf('Meetgo') > -1,
			meetgoDummy: infoService?.indexOf('Meetgo') > -1 && base.manage?.dummy,
			preDelivery: infoService?.indexOf('PreDelivery') > -1,
			encarDiagnosis: base.view?.encarDiagnosis > -1,
			encarMeetGo: base.view?.encarMeetGo === 0,
			encarCheck: base?.advertisement?.encarCheck === true,
		}),
		[infoService, base],
	);

	// 판매자구분
	const isSellerType = useMemo(
		() => ({
			isIndividaul: base.contact?.userType === 'CLIENT',
			isRepeat: base.contact?.userType === 'REPEAT',
			isDealer: base.contact?.userType === 'DEALER',
			isBrand: base.partnership?.brand !== undefined && base.partnership?.brand !== null,
			isApprovedRent: base.advertisement?.advertisementType === 'RENT_CAR' && rent?.company?.use, // 렌트제휴
			isApprovedLease: leaseSuccession?.[0]?.partnershipAdvertisementFlag, // 리스제휴
		}),
		[base, rent?.company?.use, leaseSuccession],
	);

	// 판매방식
	const infoSellType = base.advertisement?.advertisementType;
	const isSellType = useMemo(
		() => ({
			normal: infoSellType === 'NORMAL',
			leaseRent: infoSellType?.indexOf('LEASE') > -1 || infoSellType === 'RENT_SUCCESSION' || infoSellType === 'RENT_CAR',
			lease: infoSellType?.indexOf('LEASE') > -1, // 리스승계
			leaseOperating: infoSellType === 'OPERATING_LEASE', // 운융리스
			leaseFinancing: infoSellType === 'FINANCING_LEASE', // 금융리스
			rentSuccession: infoSellType === 'RENT_SUCCESSION', // 렌트승계
			rent: infoSellType === 'RENT_CAR', // 렌트(RENT) => 장기렌트(RENT_CAR)로 변경 240117
			approvedRent: infoSellType === 'RENT_CAR' && rent?.company?.use, // 렌트제휴
			approvedAffiliationLease: leaseSuccession?.[0]?.partnershipAdvertisementFlag, // 리스제휴
		}),
		[infoSellType, rent?.company?.use, leaseSuccession],
	);

	// 리스렌트
	const isLeaseRent = useMemo(
		() => ({
			leaseData: leaseSuccession.length > 0,
			rentData: !!rent.carId,
			rentSuccessionData: rentSuccession.length > 0,
			leaseSuccessionPrice: leaseSuccession.length > 0 && leaseSuccession[0].succession > 0, // 리스 승계지원금
			oldLease: leaseSuccession.length > 0 && !leaseSuccession[0].companyNo, // 구 리스승계
			rentSuccessionPrice: rentSuccession.length > 0 && rentSuccession[0].succession > 0, // 렌트 승계지원금
		}),
		[leaseSuccession, rent?.carId, rentSuccession],
	);

	// 현대캐피탈 리스제휴 광고 여부 (readside partnership.lease 존재 + 리스승계 companyNo) => 하단 CTA '상담 신청하기'
	const isHyundaiCapitalLease = Boolean(detailFlags?.isAffiliateLease) && leaseSuccession?.[0]?.companyNo === HYUNDAI_CAPITAL_COMPANY_NO;

	// 성능점검기록부
	const isInspection = useMemo(
		() => ({
			loaded: !!base.condition?.inspection, // 값들의 초기값이 false여서, 값 판단 여부를 알 수 없어 로드 플래그 추가
			all: base.condition?.inspection.formats.length > 0,
			table: base.condition?.inspection.formats.indexOf('TABLE') > -1,
			image: base.condition?.inspection.formats.indexOf('IMAGE') > -1,
		}),
		[base],
	);

	// 사고이력조회
	const isRecordView = base.condition?.accident?.recordView;

	// 당신의 다음차는(앱)에서 진입 여부
	const isEnterFromNextcar = location?.search?.indexOf('nextcar=true') > -1;

	// 금융 모델 추천에서 유입 여부
	const isLoanRecommend = location?.search?.indexOf('isLoan=true') > -1;

	// 제휴딜러 한도조회 유입 여부
	const isLimitCheck = location?.search?.indexOf('isLimitCheck=Y') > -1;

	// 오마이콜에서 유입 여부
	const isOhMyCall = location?.search?.indexOf('mycall=Y') > -1;

	// 믿고 방문예약 상세에서 유입 여부
	const isMeetgoDetail = location?.search?.indexOf('meetgoDetail=Y') > -1;

	// 신차가격
	const basePrice = base.advertisement?.price;
	const baseOriginPrice = base.category?.originPrice;
	const baseManufacturerCd = base.category?.manufacturerCd;
	const isOriginPrice =
		Boolean(baseOriginPrice && !isSellType.leaseRent) &&
		baseManufacturerCd !== '049' &&
		baseManufacturerCd !== '069' &&
		baseManufacturerCd !== '047' &&
		baseManufacturerCd !== '053' &&
		baseManufacturerCd !== '084' &&
		baseManufacturerCd !== '050' &&
		baseManufacturerCd !== '070' &&
		baseManufacturerCd !== '041' &&
		baseManufacturerCd !== '015';
	// 신차가격 + 선택옵션가격
	const resultNewcarPrice = parseInt((basePrice / (baseOriginPrice + choiceDataTotalPrice)) * 100, 10);

	return {
		infoStatus,

		isNonAdMyCar,
		isStatus,
		isService,
		isSellType,
		isLeaseRent,
		isHyundaiCapitalLease,
		isInspection,
		isRecordView,
		isOriginPrice,
		isSellerType,
		isEnterFromNextcar,
		isLoanRecommend,
		isLimitCheck,
		isOhMyCall,
		isMeetgoDetail,
		resultNewcarPrice,
	};
}
